import { type SupplierSummary, api } from '@/api/client'
import { SupplierStatusBadge } from '@/components/shared/SupplierStatusBadge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/cn'
import { useQuery } from '@tanstack/react-query'
import { Check, Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  value: SupplierSummary | null
  onChange: (supplier: SupplierSummary | null) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  /** When true, only `qualified` suppliers can be selected (default true). */
  qualifiedOnly?: boolean
}

export function SupplierPicker({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  qualifiedOnly = true,
}: Props) {
  const { t } = useTranslation('suppliers')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 300)
    return () => window.clearTimeout(id)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const search = useQuery({
    queryKey: ['suppliers', 'search', debounced],
    queryFn: () => api.suppliers.search(debounced),
    enabled: open && debounced.length >= 1,
    staleTime: 30_000,
  })

  const allResults: SupplierSummary[] = search.data?.data ?? []
  const results = qualifiedOnly
    ? allResults.filter((s) => s.qualification_status === 'qualified')
    : allResults

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {value ? (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-foreground">
                {value.legal_name}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">{value.ref}</span>
            </div>
            <SupplierStatusBadge status={value.qualification_status} className="ml-auto" />
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              onChange(null)
              setQuery('')
              setOpen(true)
            }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="clear"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            disabled={disabled}
            placeholder={placeholder ?? t('vendor_bill.supplier_picker_placeholder')}
            className="pl-10"
            onChange={(e) => {
              setQuery(e.target.value)
              if (!open) setOpen(true)
            }}
            onFocus={() => setOpen(true)}
          />
        </div>
      )}

      {open && !value ? (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-border bg-background shadow-lg">
          {debounced.length < 1 ? (
            <p className="px-4 py-3 text-xs text-muted-foreground">
              {t('vendor_bill.supplier_picker_placeholder')}
            </p>
          ) : search.isLoading ? (
            <p className="px-4 py-3 text-xs text-muted-foreground">…</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-3 text-xs text-muted-foreground">
              {t('vendor_bill.supplier_picker_empty')}
            </p>
          ) : (
            <ul className="py-1">
              {results.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(s)
                      setQuery('')
                      setOpen(false)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium text-foreground">{s.legal_name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">{s.ref}</span>
                    </div>
                    <SupplierStatusBadge status={s.qualification_status} />
                    {value && (value as SupplierSummary).id === s.id ? (
                      <Check className="h-4 w-4 text-muted-foreground" />
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
